import { resolveTableMetricAccentColor } from '@/features/tests/components/testsTableShared';
import { s } from '@/theme/tokens';
import { Box, Text } from '@/ui/primitives';
import type { Theme } from '@mui/material/styles';
import type { ReactElement } from 'react';

type MiniMetricHeaderTone =
  | 'neutral'
  | 'green'
  | 'red'
  | 'orange'
  | 'brown'
  | 'blue'
  | 'purple'
  | 'teal';

export type MiniMetricChartSummary = {
  valueLabel: string;
  valueTone: MiniMetricHeaderTone;
  deltaLabel: string | null;
  deltaValue: number | null;
  deltaTone: 'positive' | 'negative' | 'neutral';
  isMissing: boolean;
};

type MiniMetricChartHeaderProps = {
  title: string;
  summary: MiniMetricChartSummary | null;
  valueFontSize?: string | number;
};

const deltaTextSx = {
  color: 'text.secondary',
  fontSize: 'subtitle2.fontSize',
  lineHeight: '18px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: s('xs'),
  whiteSpace: 'nowrap',
} as const;

export const MiniMetricChartHeader = ({
  title,
  summary,
  valueFontSize,
}: MiniMetricChartHeaderProps): ReactElement => {
  const deltaLabel = summary?.deltaLabel ?? null;
  const deltaValue = summary?.deltaValue ?? null;
  const deltaTone = summary?.deltaTone ?? 'neutral';
  const showDeltaArrow =
    deltaLabel !== null && deltaLabel !== 'No change' && deltaValue !== null && deltaValue !== 0;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: s('sm'),
      }}
    >
      <Text
        role="label"
        sx={{
          color: 'text.secondary',
          fontSize: 'subtitle1.fontSize',
          lineHeight: 1.4,
          flex: '1 1 auto',
          minWidth: 0,
        }}
      >
        {title}
      </Text>
      {summary ? (
        <Box
          sx={{
            minWidth: 0,
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: s('sm'),
            rowGap: s('xxs'),
            flex: '1 1 auto',
            flexWrap: 'wrap',
            textAlign: 'right',
          }}
        >
          <Box
            component="span"
            sx={{
              color: summary.isMissing
                ? 'text.secondary'
                : (theme) => resolveTableMetricAccentColor(theme, summary.valueTone),
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: valueFontSize ?? ((theme: Theme) => theme.typography.subtitle1.fontSize),
              lineHeight:
                valueFontSize !== undefined
                  ? '1.2'
                  : (theme: Theme) => theme.typography.subtitle1.lineHeight,
              fontWeight: (theme) => theme.typography.fontWeightRegular,
            }}
          >
            {summary.valueLabel}
          </Box>
          {deltaLabel ? (
            <Box component="span" sx={deltaTextSx}>
              {deltaLabel}
              {showDeltaArrow ? (
                <Box
                  component="span"
                  sx={{
                    color:
                      deltaTone === 'positive'
                        ? 'success.main'
                        : deltaTone === 'negative'
                          ? 'error.main'
                          : 'text.secondary',
                    fontSize: 'subtitle2.fontSize',
                    lineHeight: 1,
                  }}
                >
                  {deltaTone === 'positive' ? '▲' : '▼'}
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};
